// ── 타입 ──────────────────────────────────────────────────
export interface CatanGame {
  matchId:              number
  roomCode:             string
  seed:                 number
  status:               string
  currentTurnUserCode:  number | null
  currentPhase:         string | null
  winnerUserCode?:      number | null
  // SEQ=10112 P8-CK-A — EXT_CK 4 BIT 확장 플래그 + 메트로폴리스 + 승점 목표
  isExtCk?:             boolean
  isExtSea?:            boolean
  isExtTb?:             boolean
  isExt56?:             boolean
  winScoreTarget?:      number
  metropolisPolUserCode?: number | null
  metropolisSciUserCode?: number | null
  metropolisTrdUserCode?: number | null
  // SEQ=10114 P8-CK-C — 야만인 트랙 (0~7), 6 도달 시 임박 UI 강조
  barbarianPosition?:    number
  // SEQ=10116 P8-CK-E — 이벤트 다이 마지막 결과 (GameLoad 복원 / 다이 시각화)
  lastEventDie?:         number | null
  lastRedDie?:           number | null
  barbarianInvasionCount?: number
}

// SEQ=10114 P8-CK-C — 도시 성벽
export interface CatanCityWall {
  matchId:  number
  vertexId: number
  userCode: number
}

export interface CatanPlayer {
  matchId:                 number
  userCode:                number
  nickname:                string
  seatIndex:               number
  color:                   string
  score:                   number
  hiddenScore:             number
  isLongestRoad:           boolean
  isLargestArmy:           boolean
  knightsPlayed:           number
  /** SETUP 페이즈 직전 배치한 마을 vertex_id. PLACE_ROAD 가이드 복원에 사용. */
  lastSettlementVertexId?: number | null
  // SEQ=10112 P8-CK-A — EXT_CK 도시 개선 단계 (0~5)
  cityImprovePol?:         number
  cityImproveSci?:         number
  cityImproveTrd?:         number
  // SEQ=10115 P8-CK-D — Defender of Catan 누적 (+1점/장, 공개)
  defenderOfCatan?:        number
  /** 진보카드 핸드 한도 (도시개선 3+ → +1, 기본 4) */
  progressCardLimit?:      number
}

// SEQ=10115 P8-CK-D — 진보 카드 75장
export interface CatanProgressCard {
  cardId:    number
  category:  'POL' | 'SCI' | 'TRD'
  cardType:  string  // 'POL_ALCHEMIST' 등 CARD_TYPE 그대로
}

export interface ProgressCardDrawnPayload {
  matchId:  number
  userCode: number
  cardId:   number
  cardType: string
  category: 'POL' | 'SCI' | 'TRD'
}

export interface ProgressCardUsedPayload {
  matchId:     number
  userCode:    number
  cardId:      number
  cardType:    string
  isPermanent: boolean
  resultJson:  string
  effectJson?: string  // SEQ=10117 P8-CK-F1: 36 효과 라우터 결과 (sp_CatanCKApplyProgressEffect)
}

export interface PermanentCardAcquiredPayload {
  matchId:  number
  userCode: number
  cardType: string
}

// SEQ=10113 P8-CK-B — EXT_CK 기사 (Knight)
export interface CatanKnight {
  knightId:  number
  matchId:   number
  userCode:  number
  level:     number          // 1=Basic, 2=Strong, 3=Mighty
  isActive:  boolean
  vertexId:  number
}

export interface CatanHex {
  hexId:        number
  q:            number
  r:            number
  resourceType: string
  numberToken:  number | null
  hasRobber:    boolean
}

export interface CatanVertex {
  vertexId:      number
  ownerUserCode: number | null
  buildingType:  string | null
}

export interface CatanEdge {
  edgeId:        number
  ownerUserCode: number | null
  buildingType:  string | null
}

export interface CatanPort {
  portId:       number
  vertexId1:    number
  vertexId2:    number
  ratio:        number
  resourceType: string | null
}

// ── Phase 3 페이로드 타입 ─────────────────────────────────
export interface BuildingPlacedPayload {
  vertexId:     number
  userCode:     number
  color:        string
  buildingType: string
}

export interface RoadPlacedPayload {
  edgeId:   number
  userCode: number
  color:    string
}

export interface TurnAdvancedPayload {
  status:              string
  currentTurnUserCode: number | null
  currentPhase:        string | null
}

export interface ResourceGrant {
  type:  string
  count: number
}

// ── Phase 4-1 페이로드 타입 ───────────────────────────────

export interface DiceRolledPayload {
  matchId:  number
  userCode: number
  dice1:    number
  dice2:    number
  total:    number
  // SEQ=10116 P8-CK-E — EXT_CK ON 시 동반 (OFF는 null)
  eventDie?: number | null
  redDie?:   number | null
}

// SEQ=10116 P8-CK-E — 이벤트 다이 자동 추첨 결과 broadcast 페이로드
export interface ProgressCardAutoDrawnPayload {
  matchId:   number
  eventDie:  number | null
  redDie:    number | null
  /** JSON 배열: [{user_code, card_id, card_type, category}] */
  drawsJson: string
}

export interface AutoDrawItem {
  user_code: number
  card_id:   number
  card_type: string
  category:  'POL' | 'SCI' | 'TRD'
}

// SEQ=10116 P8-CK-E — 메트로폴리스 변경 broadcast 페이로드
export interface MetropolisChangedPayload {
  matchId:  number
  userCode: number
  category: 'POL' | 'SCI' | 'TRD'
  newLevel: number
}

export interface ResourceGrantedPayload {
  userCode: number
  // SP는 'type' 필드로 보내고 Phase 4-1 C# DAL은 'resourceType'으로 보냄 — 양쪽 호환
  grants:   { resourceType?: string; type?: string; count: number }[]
}

export interface ResourceCountUpdatedPayload {
  userCode:   number
  totalCount: number
}

// ── Phase 4-2 페이로드 타입 ───────────────────────────────

export interface RobberPhaseStartedPayload {
  nextPhase:        string  // 'MAIN_TURN_DISCARD' | 'MAIN_TURN_MOVE_ROBBER'
  pendingDiscards:  { userCode: number; required: number }[]
}

export interface RobberMovedPayload {
  hexId:    number
  userCode: number  // 이동시킨 사람
}

export interface CardsDiscardedPayload {
  userCode:        number
  totalDiscarded:  number  // 종류 비공개, 카운트만
}

export interface CardsStolenPayload {
  thiefUserCode:   number
  victimUserCode:  number
  // resourceType 제거 (Track D-4, 2026-05-06): 그룹 broadcast에는 자원 종류 미포함
  // 가해자(Caller)는 별도 CardsStolenDetail 이벤트로 자원 종류 인지
}

export interface CardsStolenDetailPayload {
  thiefUserCode:   number
  victimUserCode:  number
  resourceType:    string  // Caller(가해자)만 받음 (Track D-4 2026-05-06)
}

export interface MyResourcesSyncPayload {
  resources: { resourceType: string; count: number }[]
}

/** PlayerResourcesSync — 그룹 broadcast (MONOPOLY 등 영향받은 모든 플레이어 자원 상세, 2026-05-07 fix)
 *   각 클라가 본인 userCode 매칭 시만 store _myResources 갱신. */
export interface PlayerResourcesSyncPayload {
  userCode:  number
  resources: { resourceType: string; count: number }[]
}

// ── Phase 6 발전 카드 페이로드 타입 ───────────────────────

/** 발전 카드 종류 */
export type DevCardType = 'KNIGHT' | 'ROAD_BUILDING' | 'MONOPOLY' | 'YEAR_OF_PLENTY' | 'VICTORY_POINT'

/** 본인 보유 카드 1장 */
export interface MyDevCard {
  cardId:        number
  cardType:      DevCardType
  boughtTurnNo:  number
  used:          boolean
  usedTurnNo:    number | null
}

/** GetMyDevCards 응답 (Caller 전용) */
export interface MyDevCardsSyncPayload {
  cards:               MyDevCard[]
  deckRemaining:       number
  largestArmyUserCode: number | null
}

/** BuyDevCard 응답 (Caller 전용 — 본인 신규 카드 1장) */
export interface DevCardBoughtPayload {
  cardId:   number
  cardType: DevCardType
}

/** 발전 카드 카운트 변동 (그룹, 본인 외엔 보유 카드 카운트만 보유 — 종류 비공개) */
export interface DevCardCountChangedPayload {
  userCode: number
  delta:    number  // +1 (구매) — 사용 시는 카드 소진 아님(보유 → USED 표기). 구매 시에만 +1.
}

/** 덱 잔량 갱신 (그룹) */
export interface DevCardDeckChangedPayload {
  remaining: number
}

/** 발전 카드 사용 통지 (그룹 — 종류 공개) */
export interface DevCardPlayedPayload {
  userCode:     number
  cardId:       number
  cardType:     DevCardType
  // KNIGHT: stolen 자원은 별도 CardsStolen 이벤트
  // ROAD_BUILDING: 도로 2개는 RoadPlaced 이벤트로 별도
  // MONOPOLY: 추가 필드 resourceType + totalTaken
  resourceType?: string
  totalTaken?:   number
  // YEAR_OF_PLENTY: 추가 필드 resource1, resource2
  resource1?:    string
  resource2?:    string
}

/** 기사단 승점 변경 (그룹) */
export interface LargestArmyChangedPayload {
  toUserCode:   number
  fromUserCode?: number | null  // 단계 5: SP에서 보내지 않음 (호환성)
  knightCount?:  number  // 단계 5 신규
}

// ── 단계 5 (Phase 7) 페이로드 타입 ─────────────────────────

/** 가장 긴 도로 변경 (그룹) */
export interface LongestRoadChangedPayload {
  toUserCode: number | null  // 박탈 시 null
  roadLength: number
}

export interface AllScoresPlayer {
  userCode:            number
  visiblePoints:       number  // 다른 플레이어가 보는 점수 (마을+도시+보너스, VP 카드 마스킹)
  settlementPts:       number
  cityPts:             number
  longestRoadPts:      number
  largestArmyPts:      number
  isLongestRoadHolder: boolean
  isLargestArmyHolder: boolean
}

/** 모든 플레이어 점수 동기화 (그룹/Caller) */
export interface AllScoresSyncPayload {
  scores: AllScoresPlayer[]
}

/** 본인 총 승점 동기화 (Caller, VP 카드 포함) */
export interface MyVictoryPointsSyncPayload {
  totalPoints:    number
  settlementPts:  number
  cityPts:        number
  vpCardPts:      number
  longestRoadPts: number
  largestArmyPts: number
}

/** 게임 종료 (그룹) */
export interface GameEndedPayload {
  winnerUserCode: number
  winnerPoints:   number
}

// ── Phase 5 거래 payload 타입 (5 broadcast + 자원 sync 보조) ──

/** 거래 제안 등록 (그룹) */
export interface TradeProposedPayload {
  tradeId:          number
  proposerUserCode: number
  targetUserCode:   number | null   // null=전체
  giveJson:         string          // {"WOOD":1,...}
  getJson:          string
  parentTradeId:    number | null
}

/** 거래 응답 (그룹) — REJECT 또는 COUNTER */
export interface TradeRespondedPayload {
  tradeId:           number
  responderUserCode: number
  action:            'REJECT' | 'COUNTER'
  counterTradeId?:   number    // COUNTER 시
}

/** COUNTER 신설 trade_id (그룹, 별도 이벤트) */
export interface TradeCounterProposedPayload {
  parentTradeId:    number
  counterTradeId:   number
  proposerUserCode: number      // 카운터의 신규 제안자 = 원본 응답자
  targetUserCode:   number      // 카운터의 대상 = 원본 제안자
  giveJson:         string
  getJson:          string
}

/** 거래 완료 (그룹) — ACCEPT/BANK/PORT */
export interface TradeCompletedPayload {
  tradeId:           number | null    // BANK/PORT는 null
  kind:              'PLAYER' | 'BANK' | 'PORT'
  // PLAYER 분기
  proposerUserCode?: number
  accepterUserCode?: number
  giveJson?:         string
  getJson?:          string
  // BANK/PORT 분기
  userCode?:         number
  giveType?:         string
  giveQty?:          number
  getType?:          string
  getQty?:           number
  portId?:           number      // PORT 분기
  ratio?:            number      // PORT 분기
}

/** 거래 취소 (그룹) — 제안자 취소 또는 EndTurn 자동 cancel 또는 전원 거부 자동 취소 */
export interface TradeCancelledPayload {
  tradeId:          number
  proposerUserCode: number
  reason:           'USER_CANCEL' | 'END_TURN' | 'ALL_REJECTED'
}

/** 제안자 자원 정확값 그룹 broadcast (클라가 본인 userCode 필터, 정보 비대칭 우회) */
export interface ProposerResourcesSyncPayload {
  userCode:  number
  resources: Array<{ resourceType: string; count: number }>
}

/** 진행 중 거래 요약 (스토어 derived state) */
export interface OpenTradeView {
  tradeId:          number
  proposerUserCode: number
  targetUserCode:   number | null
  giveJson:         string
  getJson:          string
  parentTradeId:    number | null
  myResponseAction: 'ACCEPT' | 'REJECT' | 'COUNTER' | null  // 본인이 이 trade에 응답한 적 있는지
}

// ── 내부 상태 ────────────────────────────────────────────
let _game:             CatanGame | null = null
let _players:          CatanPlayer[]   = []
let _hexes:            CatanHex[]      = []
let _vertices:         CatanVertex[]   = []
let _edges:            CatanEdge[]     = []
let _ports:            CatanPort[]     = []
// Phase 3 자원 상태
let _myResources:      Record<string, number> = {}
let _resourceCounts:   Record<number, number> = {}  // userCode → 총 카드 수
// Phase 4-1 주사위 상태
let _lastDice:         DiceRolledPayload | null = null
// Phase 4-2 도적/폐기 상태
let _pendingDiscards:  Record<number, { required: number; done: boolean }> = {}
let _myDiscardRequired: number = 0
// Phase 6 발전 카드 상태 (본인 보유는 종류·CARD_ID 까지, 타인은 카운트만)
let _myDevCards:           MyDevCard[] = []
let _devDeckRemaining:     number = 25
let _largestArmyUserCode:  number | null = null
let _otherDevCardCounts:   Record<number, number> = {}  // userCode → 보유 카드 수 (종류 비공개)

// SEQ=10113 P8-CK-B — 기사 (Knight) 상태
let _knights: CatanKnight[] = []

// SEQ=10114 P8-CK-C — 야만인 트랙 + 도시 성벽 + 최근 침공 결과
let _barbarianPosition: number = 0
let _cityWalls: CatanCityWall[] = []
export interface BarbarianInvasionPayload {
  logId:               number
  invasionNo:          number
  outcome:             'WIN' | 'LOSE'
  strengthSum:         number
  barbarians:          number
  defenderUserCodes:   number[]
  loserUserCodes:      number[]
  loserVertexIds:      number[]
}
let _lastInvasion: BarbarianInvasionPayload | null = null

// SEQ=10115 P8-CK-D — 본인 진보 카드 핸드 + 매치 전체 영구 카드 컬렉션
let _myProgressCards: CatanProgressCard[] = []
/** 다른 플레이어 진보 카드 보유 개수 (개수만 공개, 종류는 비공개) */
let _otherProgressCardCounts: Record<number, number> = {}
/** 영구 카드 컬렉션 (매치 전체 공개) — userCode → cardType[] */
let _permanentCards: Record<number, string[]> = {}

// 단계 5 (Phase 7) 상태
let _longestRoadUserCode: number | null = null
let _allScores:           AllScoresPlayer[] = []
let _myVictoryPoints:     MyVictoryPointsSyncPayload | null = null
let _gameEnded:           GameEndedPayload | null = null

// Phase 5 거래 상태
let _openTrades:          OpenTradeView[]                                       = []   // 진행 중 거래 요약
let _myResponses:         Record<number, 'ACCEPT' | 'REJECT' | 'COUNTER'>       = {}   // tradeId → 본인 응답 액션
let _counterUsedTrades:   Set<number>                                           = new Set() // 본인이 카운터 한 trade_id (1회 한정 UX)
/** tradeId → { userCode → 'ACCEPT'|'REJECT'|'COUNTER' } — 전체 응답자 추적 (요청자 결과 표시용) */
let _tradeAllResponses:   Record<number, Record<number, 'ACCEPT' | 'REJECT' | 'COUNTER'>> = {}

type Listener = () => void
const _listeners: Set<Listener> = new Set()

function _notify() {
  _listeners.forEach(fn => fn())
}

// ── 구독 ─────────────────────────────────────────────────
export function subscribeCatan(fn: Listener) {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

// ── 조회 ─────────────────────────────────────────────────
export const catanStore = {
  getGame():           CatanGame | null         { return _game },
  getPlayers():        CatanPlayer[]            { return _players },
  getHexes():          CatanHex[]               { return _hexes },
  getVertices():       CatanVertex[]            { return _vertices },
  getEdges():          CatanEdge[]              { return _edges },
  getPorts():          CatanPort[]              { return _ports },
  getMyResources():    Record<string, number>   { return _myResources },
  getResourceCount(userCode: number): number    { return _resourceCounts[userCode] ?? 0 },
  getResourceCounts(): Record<number, number>   { return _resourceCounts },
  // Phase 4-1
  getLastDice():       DiceRolledPayload | null  { return _lastDice },
  // Phase 4-2
  getPendingDiscards(): Record<number, { required: number; done: boolean }> { return _pendingDiscards },
  getMyDiscardRequired(): number { return _myDiscardRequired },
  setMyDiscardRequired(n: number) { _myDiscardRequired = n; _notify() },
  // Phase 6
  getMyDevCards(): MyDevCard[] { return _myDevCards },
  getDevDeckRemaining(): number { return _devDeckRemaining },
  getLargestArmyUserCode(): number | null { return _largestArmyUserCode },
  getOtherDevCardCount(userCode: number): number { return _otherDevCardCounts[userCode] ?? 0 },

  // SEQ=10113 P8-CK-B — 기사 (Knight)
  getKnights(): CatanKnight[] { return _knights },
  getMyKnights(myUserCode: number): CatanKnight[] {
    return _knights.filter(k => k.userCode === myUserCode)
  },
  getKnightAtVertex(vertexId: number): CatanKnight | null {
    return _knights.find(k => k.vertexId === vertexId) ?? null
  },
  /** 본인 기사 활성 LEVEL 합 (P8-CK-C 야만인 방어 표시 미리보기) */
  getMyKnightStrengthSum(myUserCode: number): number {
    return _knights
      .filter(k => k.userCode === myUserCode && k.isActive)
      .reduce((s, k) => s + k.level, 0)
  },
  /** Hub 이벤트 핸들러용 */
  upsertKnight(k: CatanKnight) {
    const i = _knights.findIndex(x => x.knightId === k.knightId)
    if (i >= 0) _knights[i] = k
    else _knights = [..._knights, k]
    _notify()
  },
  removeKnight(knightId: number) {
    _knights = _knights.filter(k => k.knightId !== knightId)
    _notify()
  },
  setKnightActive(knightId: number, isActive: boolean) {
    const i = _knights.findIndex(k => k.knightId === knightId)
    if (i >= 0) { _knights[i] = { ..._knights[i], isActive }; _notify() }
  },
  setKnightLevel(knightId: number, level: number) {
    const i = _knights.findIndex(k => k.knightId === knightId)
    if (i >= 0) { _knights[i] = { ..._knights[i], level }; _notify() }
  },
  setKnightVertex(knightId: number, vertexId: number, isActive?: boolean) {
    const i = _knights.findIndex(k => k.knightId === knightId)
    if (i >= 0) {
      _knights[i] = { ..._knights[i], vertexId, isActive: isActive ?? _knights[i].isActive }
      _notify()
    }
  },
  /** 본인 모든 기사 비활성화 (KnightsDeactivated 이벤트) */
  deactivateAllKnightsOf(userCode: number) {
    let changed = false
    _knights = _knights.map(k => {
      if (k.userCode === userCode && k.isActive) { changed = true; return { ...k, isActive: false } }
      return k
    })
    if (changed) _notify()
  },

  // SEQ=10114 P8-CK-C — 야만인 트랙 + 도시 성벽
  getBarbarianPosition(): number { return _barbarianPosition },
  setBarbarianPosition(pos: number) { _barbarianPosition = pos; _notify() },
  getCityWalls(): CatanCityWall[] { return _cityWalls },
  getCityWallAtVertex(vertexId: number): CatanCityWall | null {
    return _cityWalls.find(w => w.vertexId === vertexId) ?? null
  },
  upsertCityWall(w: CatanCityWall) {
    const i = _cityWalls.findIndex(x => x.vertexId === w.vertexId)
    if (i >= 0) _cityWalls[i] = w
    else _cityWalls = [..._cityWalls, w]
    _notify()
  },
  removeCityWall(vertexId: number) {
    _cityWalls = _cityWalls.filter(w => w.vertexId !== vertexId)
    _notify()
  },
  /** 침공 패배 시 LOSER vertex의 도시→마을 + 성벽 제거 일괄 처리 */
  applyInvasionLoss(loserVertexIds: number[]) {
    let changed = false
    for (const v of loserVertexIds) {
      // 도시→마을: vertex 자체는 BUILDING_TYPE 변경. _vertices state 갱신은 별도 GameState refetch나 vertex 이벤트로 받음.
      // 본 메서드는 성벽 제거만 즉시 반영 (UI 깜빡임 방지).
      const i = _cityWalls.findIndex(w => w.vertexId === v)
      if (i >= 0) { _cityWalls.splice(i, 1); changed = true }
      // vertex의 buildingType 'CITY'→'SETTLEMENT' 도 reflect
      const vi = _vertices.findIndex(x => x.vertexId === v)
      if (vi >= 0 && _vertices[vi].buildingType === 'CITY') {
        _vertices[vi] = { ..._vertices[vi], buildingType: 'SETTLEMENT' }
        changed = true
      }
    }
    if (changed) _notify()
  },
  getLastInvasion(): BarbarianInvasionPayload | null { return _lastInvasion },
  setLastInvasion(p: BarbarianInvasionPayload | null) { _lastInvasion = p; _notify() },

  // SEQ=10115 P8-CK-D — 진보 카드 + 영구 카드 + Defender of Catan
  getMyProgressCards(): CatanProgressCard[] { return _myProgressCards },
  addMyProgressCard(c: CatanProgressCard) {
    _myProgressCards = [..._myProgressCards, c]
    _notify()
  },
  removeMyProgressCard(cardId: number) {
    _myProgressCards = _myProgressCards.filter(c => c.cardId !== cardId)
    _notify()
  },
  /** Caller가 카드 사용 시 본인 핸드에서 제거 (영구 카드는 종류 표시는 유지하되 핸드에서 빼고 컬렉션으로 이동) */
  getOtherProgressCardCount(userCode: number): number { return _otherProgressCardCounts[userCode] ?? 0 },
  incrementOtherProgressCardCount(userCode: number, delta = 1) {
    _otherProgressCardCounts = {
      ..._otherProgressCardCounts,
      [userCode]: Math.max(0, (_otherProgressCardCounts[userCode] ?? 0) + delta)
    }
    _notify()
  },
  /** 영구 카드 컬렉션 (다른 플레이어도 공개) */
  getPermanentCards(userCode: number): string[] { return _permanentCards[userCode] ?? [] },
  getAllPermanentCards(): Record<number, string[]> { return _permanentCards },
  addPermanentCard(userCode: number, cardType: string) {
    const cur = _permanentCards[userCode] ?? []
    if (cur.includes(cardType) && cardType !== 'POL_DEFENDER_OF_CATAN') return  // 중복 무시 (Defender만 누적)
    _permanentCards = { ..._permanentCards, [userCode]: [...cur, cardType] }
    _notify()
  },
  /** 침공 WIN 시 DEFENDER_OF_CATAN +1 (CatanPlayer 카운터) */
  incrementDefenderOfCatan(userCode: number, delta = 1) {
    const i = _players.findIndex(p => p.userCode === userCode)
    if (i >= 0) {
      _players[i] = { ..._players[i], defenderOfCatan: (_players[i].defenderOfCatan ?? 0) + delta }
      _notify()
    }
  },

  // 단계 5 (Phase 7)
  getLongestRoadUserCode(): number | null { return _longestRoadUserCode },
  getAllScores(): AllScoresPlayer[] { return _allScores },
  getMyVictoryPoints(): MyVictoryPointsSyncPayload | null { return _myVictoryPoints },
  getGameEnded(): GameEndedPayload | null { return _gameEnded },

  // Phase 5 거래
  getOpenTrades(): OpenTradeView[] { return _openTrades },
  getMyResponse(tradeId: number): 'ACCEPT' | 'REJECT' | 'COUNTER' | null { return _myResponses[tradeId] ?? null },
  hasCounterUsed(tradeId: number): boolean { return _counterUsedTrades.has(tradeId) },
  /** 본인이 진행 중 OPEN으로 제안한 거래 (D-4: 1건만) */
  getMyOpenProposal(myUserCode: number): OpenTradeView | null {
    return _openTrades.find(t => t.proposerUserCode === myUserCode && t.parentTradeId == null) ?? null
  },
  /** 특정 거래의 전체 응답자 맵 (tradeId → { userCode → action }) */
  getTradeResponses(tradeId: number): Record<number, 'ACCEPT' | 'REJECT' | 'COUNTER'> {
    return _tradeAllResponses[tradeId] ?? {}
  },

  // ── 서버 응답 적용 ─────────────────────────────────────
  // ASP.NET Core 기본 직렬화 = camelCase. PascalCase / UPPER_CASE 안전망 매칭.
  applyGameState(raw: Record<string, unknown>) {
    // ── 게임 메타 ────────────────────────────────────────
    const matchId    = Number(raw.matchId    ?? raw.MatchId    ?? raw.MATCH_ID    ?? 0)
    const roomCode   = String(raw.roomCode   ?? raw.RoomCode   ?? raw.ROOM_CODE   ?? '')
    const seed       = Number(raw.seed       ?? raw.Seed       ?? raw.SEED        ?? 0)
    const status     = String(raw.status     ?? raw.Status     ?? raw.STATUS      ?? '')
    const ctuc       = raw.currentTurnUserCode ?? raw.CurrentTurnUserCode ?? raw.CURRENT_TURN_USER_CODE
    const cp         = raw.currentPhase     ?? raw.CurrentPhase     ?? raw.CURRENT_PHASE
    const wuc        = raw.winnerUserCode   ?? raw.WinnerUserCode   ?? raw.WINNER_USER_CODE

    // SEQ=10112 P8-CK-A — EXT_CK 4 BIT 플래그 + 메트로폴리스 + 승점 목표
    const extCk   = raw.isExtCk  ?? raw.IsExtCk  ?? raw.IS_EXT_CK
    const extSea  = raw.isExtSea ?? raw.IsExtSea ?? raw.IS_EXT_SEA
    const extTb   = raw.isExtTb  ?? raw.IsExtTb  ?? raw.IS_EXT_TB
    const ext56   = raw.isExt56  ?? raw.IsExt56  ?? raw.IS_EXT_5_6
    const wsTgt   = raw.winScoreTarget ?? raw.WinScoreTarget ?? raw.WIN_SCORE_TARGET
    const mPol    = raw.metropolisPolUserCode ?? raw.MetropolisPolUserCode ?? raw.METROPOLIS_POL_USER_CODE
    const mSci    = raw.metropolisSciUserCode ?? raw.MetropolisSciUserCode ?? raw.METROPOLIS_SCI_USER_CODE
    const mTrd    = raw.metropolisTrdUserCode ?? raw.MetropolisTrdUserCode ?? raw.METROPOLIS_TRD_USER_CODE
    // SEQ=10114 P8-CK-C — 야만인 트랙
    const barbPos = raw.barbarianPosition ?? raw.BarbarianPosition ?? raw.BARBARIAN_POSITION
    if (barbPos != null) _barbarianPosition = Number(barbPos)

    _game = {
      matchId,
      roomCode,
      seed,
      status,
      currentTurnUserCode: ctuc != null ? Number(ctuc) : null,
      currentPhase:        cp   != null ? String(cp)   : null,
      winnerUserCode:      wuc  != null ? Number(wuc)  : null,
      isExtCk:             extCk != null ? !!extCk : false,
      isExtSea:            extSea != null ? !!extSea : false,
      isExtTb:             extTb != null ? !!extTb : false,
      isExt56:             ext56 != null ? !!ext56 : false,
      winScoreTarget:      wsTgt != null ? Number(wsTgt) : 10,
      metropolisPolUserCode: mPol != null ? Number(mPol) : null,
      metropolisSciUserCode: mSci != null ? Number(mSci) : null,
      metropolisTrdUserCode: mTrd != null ? Number(mTrd) : null,
      barbarianPosition:    barbPos != null ? Number(barbPos) : _barbarianPosition,
    }

    // ── 플레이어 ──────────────────────────────────────────
    const rawPlayers = (raw.players ?? raw.Players ?? raw.PLAYERS ?? []) as Record<string, unknown>[]
    _players = rawPlayers.map(p => {
      const lsv = p.lastSettlementVertexId ?? p.LastSettlementVertexId ?? p.LAST_SETTLEMENT_VERTEX_ID
      // SEQ=10112 P8-CK-A — 도시 개선 단계 (CITY_IMPROVE_POL/SCI/TRD)
      const cip = p.cityImprovePol ?? p.CityImprovePol ?? p.CITY_IMPROVE_POL
      const cis = p.cityImproveSci ?? p.CityImproveSci ?? p.CITY_IMPROVE_SCI
      const cit = p.cityImproveTrd ?? p.CityImproveTrd ?? p.CITY_IMPROVE_TRD
      return {
        matchId:               Number(p.matchId      ?? p.MatchId      ?? p.MATCH_ID      ?? 0),
        userCode:              Number(p.userCode     ?? p.UserCode     ?? p.USER_CODE     ?? 0),
        nickname:              String(p.nickname     ?? p.Nickname     ?? p.NICKNAME      ?? ''),
        seatIndex:             Number(p.seatIndex    ?? p.SeatIndex    ?? p.SEAT_INDEX    ?? 0),
        color:                 String(p.color        ?? p.Color        ?? p.COLOR         ?? ''),
        score:                 Number(p.score        ?? p.Score        ?? p.SCORE         ?? 0),
        hiddenScore:           Number(p.hiddenScore  ?? p.HiddenScore  ?? p.HIDDEN_SCORE  ?? 0),
        isLongestRoad:         !!(p.isLongestRoad ?? p.IsLongestRoad ?? p.IS_LONGEST_ROAD),
        isLargestArmy:         !!(p.isLargestArmy ?? p.IsLargestArmy ?? p.IS_LARGEST_ARMY),
        knightsPlayed:         Number(p.knightsPlayed ?? p.KnightsPlayed ?? p.KNIGHTS_PLAYED ?? 0),
        lastSettlementVertexId: lsv != null ? Number(lsv) : null,
        cityImprovePol:        cip != null ? Number(cip) : 0,
        cityImproveSci:        cis != null ? Number(cis) : 0,
        cityImproveTrd:        cit != null ? Number(cit) : 0,
      }
    })

    // ── 헥스 ──────────────────────────────────────────────
    const rawHexes = (raw.hexes ?? raw.Hexes ?? raw.HEXES ?? []) as Record<string, unknown>[]
    _hexes = rawHexes.map(h => {
      const nt = h.numberToken ?? h.NumberToken ?? h.NUMBER_TOKEN
      return {
        hexId:        Number(h.hexId        ?? h.HexId        ?? h.HEX_ID        ?? 0),
        q:            Number(h.q            ?? h.Q                               ?? 0),
        r:            Number(h.r            ?? h.R                               ?? 0),
        resourceType: String(h.resourceType ?? h.ResourceType ?? h.RESOURCE_TYPE ?? ''),
        numberToken:  nt != null ? Number(nt) : null,
        hasRobber:    !!(h.hasRobber ?? h.HasRobber ?? h.HAS_ROBBER),
      }
    })

    // ── 정점 ──────────────────────────────────────────────
    const rawVertices = (raw.vertices ?? raw.Vertices ?? raw.VERTICES ?? []) as Record<string, unknown>[]
    _vertices = rawVertices.map(v => {
      const ouc = v.ownerUserCode ?? v.OwnerUserCode ?? v.OWNER_USER_CODE
      const bt  = v.buildingType  ?? v.BuildingType  ?? v.BUILDING_TYPE
      return {
        vertexId:      Number(v.vertexId   ?? v.VertexId   ?? v.VERTEX_ID   ?? 0),
        ownerUserCode: ouc != null ? Number(ouc) : null,
        buildingType:  bt  != null ? String(bt)  : null,
      }
    })

    // ── 변 ────────────────────────────────────────────────
    const rawEdges = (raw.edges ?? raw.Edges ?? raw.EDGES ?? []) as Record<string, unknown>[]
    _edges = rawEdges.map(e => {
      const ouc = e.ownerUserCode ?? e.OwnerUserCode ?? e.OWNER_USER_CODE
      const bt  = e.buildingType  ?? e.BuildingType  ?? e.BUILDING_TYPE
      return {
        edgeId:        Number(e.edgeId   ?? e.EdgeId   ?? e.EDGE_ID   ?? 0),
        ownerUserCode: ouc != null ? Number(ouc) : null,
        buildingType:  bt  != null ? String(bt)  : null,
      }
    })

    // ── 항구 ──────────────────────────────────────────────
    const rawPorts = (raw.ports ?? raw.Ports ?? raw.PORTS ?? []) as Record<string, unknown>[]
    _ports = rawPorts.map(p => {
      const rt = p.resourceType ?? p.ResourceType ?? p.RESOURCE_TYPE
      return {
        portId:       Number(p.portId    ?? p.PortId    ?? p.PORT_ID    ?? 0),
        vertexId1:    Number(p.vertexId1 ?? p.VertexId1 ?? p.VERTEX_ID_1 ?? 0),
        vertexId2:    Number(p.vertexId2 ?? p.VertexId2 ?? p.VERTEX_ID_2 ?? 0),
        ratio:        Number(p.ratio     ?? p.Ratio     ?? p.RATIO      ?? 3),
        resourceType: rt != null ? String(rt) : null,
      }
    })

    // ── Phase 4-2 폐기 대기 (결과셋 9) — 새로고침 복구 2026-05-07 ──────────────
    const rawDiscReq = raw.myDiscardRequired ?? raw.MyDiscardRequired ?? raw.MY_DISCARD_REQUIRED
    _myDiscardRequired = (rawDiscReq != null && Number(rawDiscReq) > 0) ? Number(rawDiscReq) : 0

    // ── 자원 (Phase 3, 결과셋 7/8) ───────────────────────
    const rawMyRes = (raw.myResources ?? raw.MyResources ?? []) as Record<string, unknown>[]
    if (rawMyRes.length > 0) {
      _myResources = {}
      for (const r of rawMyRes) {
        const t = String(r.resourceType ?? r.ResourceType ?? r.RESOURCE_TYPE ?? '')
        const c = Number(r.count ?? r.Count ?? r.COUNT ?? 0)
        if (t) _myResources[t] = c
      }
    }

    const rawResCounts = (raw.resourceCounts ?? raw.ResourceCounts ?? []) as Record<string, unknown>[]
    if (rawResCounts.length > 0) {
      _resourceCounts = {}
      for (const r of rawResCounts) {
        const uc = Number(r.userCode ?? r.UserCode ?? r.USER_CODE ?? 0)
        const c  = Number(r.totalCount ?? r.TotalCount ?? r.TOTAL_COUNT ?? 0)
        if (uc) _resourceCounts[uc] = c
      }
    }

    _notify()
  },

  // ── Phase 3 이벤트 핸들러 ─────────────────────────────

  applyBuildingPlaced(payload: BuildingPlacedPayload) {
    const idx = _vertices.findIndex(v => v.vertexId === payload.vertexId)
    if (idx >= 0) {
      _vertices = _vertices.slice()
      _vertices[idx] = {
        ..._vertices[idx],
        ownerUserCode: payload.userCode,
        buildingType:  payload.buildingType,
      }
    }
    _notify()
  },

  applyRoadPlaced(payload: RoadPlacedPayload) {
    const idx = _edges.findIndex(e => e.edgeId === payload.edgeId)
    if (idx >= 0) {
      _edges = _edges.slice()
      _edges[idx] = {
        ..._edges[idx],
        ownerUserCode: payload.userCode,
        buildingType:  'ROAD',
      }
    }
    _notify()
  },

  applyTurnAdvanced(payload: TurnAdvancedPayload) {
    if (_game) {
      _game = {
        ..._game,
        status:             payload.status,
        currentTurnUserCode: payload.currentTurnUserCode,
        currentPhase:       payload.currentPhase,
      }
      _notify()
    }
  },

  applyScoreUpdated(userCode: number, score: number) {
    const idx = _players.findIndex(p => p.userCode === userCode)
    if (idx >= 0) {
      _players = _players.slice()
      _players[idx] = { ..._players[idx], score }
    }
    _notify()
  },

  applyResourceGranted(grants: ResourceGrant[]) {
    const next = { ..._myResources }
    for (const g of grants) {
      next[g.type] = (next[g.type] ?? 0) + g.count
    }
    _myResources = next
    _notify()
  },

  applyResourceCountUpdated(userCode: number, totalCount: number) {
    _resourceCounts = { ..._resourceCounts, [userCode]: totalCount }
    _notify()
  },

  /** PlayerResourcesSync — 본인 userCode 매칭 시만 _myResources 갱신 (2026-05-07 MONOPOLY fix) */
  applyPlayerResourcesSync(payload: PlayerResourcesSyncPayload, myUserCode: number) {
    if (payload.userCode !== myUserCode) return
    const next: Record<string, number> = {}
    for (const r of payload.resources) {
      next[r.resourceType] = r.count
    }
    _myResources = next
    _notify()
  },

  // ── Phase 4-1 이벤트 핸들러 ──────────────────────────

  applyDiceRolled(payload: DiceRolledPayload) {
    _lastDice = payload
    // 게임 상태의 currentPhase를 MAIN_TURN_POSTROLL로 갱신
    if (_game) {
      _game = { ..._game, currentPhase: 'MAIN_TURN_POSTROLL' }
    }
    _notify()
  },

  applyResourceGrantedV2(payload: ResourceGrantedPayload, myUserCode: number) {
    if (payload.userCode === myUserCode) {
      const next = { ..._myResources }
      for (const g of payload.grants) {
        // SP는 'type', C# DAL은 'resourceType' — 양쪽 호환
        const rt = g.resourceType ?? g.type ?? ''
        if (rt) next[rt] = (next[rt] ?? 0) + g.count
      }
      _myResources = next
    }
    _notify()
  },

  // ── Phase 4-2 이벤트 핸들러 ──────────────────────────

  applyRobberPhaseStarted(payload: RobberPhaseStartedPayload, myUserCode: number) {
    _pendingDiscards = {}
    let myReq = 0
    for (const pd of payload.pendingDiscards) {
      _pendingDiscards[pd.userCode] = { required: pd.required, done: false }
      if (pd.userCode === myUserCode) myReq = pd.required
    }
    _myDiscardRequired = myReq
    if (_game) {
      _game = { ..._game, currentPhase: payload.nextPhase }
    }
    _notify()
  },

  applyRobberMoved(payload: RobberMovedPayload) {
    // 도적이 이동: 모든 헥스의 hasRobber=false, 대상만 true
    _hexes = _hexes.map(h => ({
      ...h,
      hasRobber: h.hexId === payload.hexId,
    }))
    _notify()
  },

  applyCardsDiscarded(payload: CardsDiscardedPayload, myUserCode: number) {
    if (_pendingDiscards[payload.userCode]) {
      _pendingDiscards = {
        ..._pendingDiscards,
        [payload.userCode]: { ..._pendingDiscards[payload.userCode], done: true },
      }
    }
    if (payload.userCode === myUserCode) {
      _myDiscardRequired = 0
    }
    _notify()
  },

  applyCardsStolen(_payload: CardsStolenPayload, _myUserCode: number) {
    // Track D-4 (2026-05-06) — 그룹 broadcast에 resourceType 미포함.
    // 가해자는 CardsStolenDetail Caller 이벤트로 자원 종류 인지하고 본인 store는 MyResourcesSync로 갱신.
    // 피해자는 본인 자원 정확값을 ResourceCountUpdated(합계) + 다음 broadcast로 추정 (정보 비대칭).
    // 즉시 반영은 안 함 (서버 권위 기반 동기화).
    _notify()
  },

  applyMyResourcesSync(payload: MyResourcesSyncPayload) {
    const next: Record<string, number> = {}
    for (const r of payload.resources) {
      next[r.resourceType] = r.count
    }
    _myResources = next
    _notify()
  },

  // ── Phase 6 발전 카드 이벤트 핸들러 ─────────────────────

  /** GetMyDevCards 응답 (Caller 전용) */
  applyMyDevCardsSync(payload: MyDevCardsSyncPayload) {
    _myDevCards = payload.cards.slice()
    _devDeckRemaining = payload.deckRemaining
    _largestArmyUserCode = payload.largestArmyUserCode
    _notify()
  },

  /** BuyDevCard 응답 (Caller 전용 — 본인 신규 카드 1장 추가) */
  applyDevCardBought(payload: DevCardBoughtPayload, currentTurnNo: number) {
    // 같은 cardId 중복 방지 (재구매 시도 시 안전)
    if (_myDevCards.some(c => c.cardId === payload.cardId)) return
    _myDevCards = _myDevCards.concat([{
      cardId:       payload.cardId,
      cardType:     payload.cardType,
      boughtTurnNo: currentTurnNo,
      used:         false,
      usedTurnNo:   null,
    }])
    _notify()
  },

  /** 그룹 - 보유 카드 카운트 변동 (구매 시 +1, 본인 제외) */
  applyDevCardCountChanged(payload: DevCardCountChangedPayload, myUserCode: number) {
    if (payload.userCode === myUserCode) return  // 본인은 _myDevCards로 추적
    const cur = _otherDevCardCounts[payload.userCode] ?? 0
    _otherDevCardCounts = { ..._otherDevCardCounts, [payload.userCode]: cur + payload.delta }
    _notify()
  },

  /** 덱 잔량 갱신 (그룹) */
  applyDevCardDeckChanged(payload: DevCardDeckChangedPayload) {
    _devDeckRemaining = payload.remaining
    _notify()
  },

  /** 카드 사용 통지 (그룹 — 본인이면 카드 used=true 표기) */
  applyDevCardPlayed(payload: DevCardPlayedPayload, myUserCode: number) {
    if (payload.userCode === myUserCode) {
      const idx = _myDevCards.findIndex(c => c.cardId === payload.cardId)
      if (idx >= 0) {
        _myDevCards = _myDevCards.slice()
        _myDevCards[idx] = { ..._myDevCards[idx], used: true }
      }
    }
    _notify()
  },

  /** 기사단 승점 변경 */
  applyLargestArmyChanged(payload: LargestArmyChangedPayload) {
    _largestArmyUserCode = payload.toUserCode
    // 플레이어 isLargestArmy 플래그도 갱신
    _players = _players.map(p => ({
      ...p,
      isLargestArmy: p.userCode === payload.toUserCode,
    }))
    _notify()
  },

  // ── 단계 5 (Phase 7) 핸들러 ──────────────────────────

  /** 가장 긴 도로 변경 */
  applyLongestRoadChanged(payload: LongestRoadChangedPayload) {
    _longestRoadUserCode = payload.toUserCode
    _players = _players.map(p => ({
      ...p,
      isLongestRoad: p.userCode === payload.toUserCode,
    }))
    _notify()
  },

  /** 모든 플레이어 점수 동기화 */
  applyAllScoresSync(payload: AllScoresSyncPayload) {
    _allScores = payload.scores
    // 보너스 보유자 동기화 (별도 LongestRoad/LargestArmyChanged 없이 진입/재연결 시에도 갱신)
    const lrHolder = payload.scores.find(s => s.isLongestRoadHolder)
    const laHolder = payload.scores.find(s => s.isLargestArmyHolder)
    _longestRoadUserCode = lrHolder?.userCode ?? null
    _largestArmyUserCode = laHolder?.userCode ?? null
    _players = _players.map(p => ({
      ...p,
      isLongestRoad: p.userCode === _longestRoadUserCode,
      isLargestArmy: p.userCode === _largestArmyUserCode,
    }))
    _notify()
  },

  /** 본인 총 승점 동기화 (VP 카드 포함) */
  applyMyVictoryPointsSync(payload: MyVictoryPointsSyncPayload) {
    _myVictoryPoints = payload
    _notify()
  },

  /** 게임 종료 */
  applyGameEnded(payload: GameEndedPayload) {
    _gameEnded = payload
    if (_game) {
      _game = { ..._game, status: 'GAME_END', winnerUserCode: payload.winnerUserCode }
    }
    _notify()
  },

  // ── Phase 5 거래 apply 핸들러 ───────────────────────────

  /** 거래 제안 등록 — _openTrades에 추가 */
  applyTradeProposed(payload: TradeProposedPayload) {
    const view: OpenTradeView = {
      tradeId:          payload.tradeId,
      proposerUserCode: payload.proposerUserCode,
      targetUserCode:   payload.targetUserCode,
      giveJson:         payload.giveJson,
      getJson:          payload.getJson,
      parentTradeId:    payload.parentTradeId,
      myResponseAction: null,
    }
    _openTrades = [..._openTrades.filter(t => t.tradeId !== payload.tradeId), view]
    _notify()
  },

  /** 거래 응답 (REJECT 또는 COUNTER) — myResponses + 전체 응답자 맵 갱신 */
  applyTradeResponded(payload: TradeRespondedPayload, myUserCode: number) {
    if (payload.responderUserCode === myUserCode) {
      _myResponses = { ..._myResponses, [payload.tradeId]: payload.action }
      if (payload.action === 'COUNTER') _counterUsedTrades.add(payload.tradeId)
    }
    // 전체 응답자 맵 갱신 (요청자 결과 표시용)
    _tradeAllResponses = {
      ..._tradeAllResponses,
      [payload.tradeId]: {
        ...(_tradeAllResponses[payload.tradeId] ?? {}),
        [payload.responderUserCode]: payload.action,
      },
    }
    // openTrades view에 본인 응답 액션 기록
    _openTrades = _openTrades.map(t =>
      t.tradeId === payload.tradeId && payload.responderUserCode === myUserCode
        ? { ...t, myResponseAction: payload.action }
        : t
    )
    // COUNTER인 경우 원본 OPEN을 종결 처리 (서버 STATUS=COUNTERED) — _openTrades에서 제거
    if (payload.action === 'COUNTER') {
      _openTrades = _openTrades.filter(t => t.tradeId !== payload.tradeId)
    }
    _notify()
  },

  /** COUNTER 신설 trade — _openTrades에 신규 view 추가 */
  applyTradeCounterProposed(payload: TradeCounterProposedPayload) {
    const view: OpenTradeView = {
      tradeId:          payload.counterTradeId,
      proposerUserCode: payload.proposerUserCode,
      targetUserCode:   payload.targetUserCode,
      giveJson:         payload.giveJson,
      getJson:          payload.getJson,
      parentTradeId:    payload.parentTradeId,
      myResponseAction: null,
    }
    _openTrades = [..._openTrades.filter(t => t.tradeId !== payload.counterTradeId), view]
    _notify()
  },

  /** 거래 완료 (ACCEPT/BANK/PORT) — _openTrades에서 제거 (PLAYER 분기) + ACCEPT 응답자 기록 */
  applyTradeCompleted(payload: TradeCompletedPayload) {
    if (payload.kind === 'PLAYER' && payload.tradeId != null) {
      // 수락자 기록 (요청자 결과 표시용)
      if (payload.accepterUserCode != null) {
        _tradeAllResponses = {
          ..._tradeAllResponses,
          [payload.tradeId]: {
            ...(_tradeAllResponses[payload.tradeId] ?? {}),
            [payload.accepterUserCode]: 'ACCEPT',
          },
        }
      }
      _openTrades = _openTrades.filter(t => t.tradeId !== payload.tradeId)
    }
    _notify()
  },

  /** 거래 취소 — _openTrades에서 제거 */
  applyTradeCancelled(payload: TradeCancelledPayload) {
    _openTrades = _openTrades.filter(t => t.tradeId !== payload.tradeId)
    _notify()
  },

  /** 제안자 자원 정확값 (ACCEPT 후 그룹 broadcast — 클라가 본인 userCode 필터) */
  applyProposerResourcesSync(payload: ProposerResourcesSyncPayload, myUserCode: number) {
    if (payload.userCode !== myUserCode) return  // 정보 비대칭 — 본인 것만 처리
    const next: Record<string, number> = {}
    for (const r of payload.resources) {
      next[r.resourceType] = r.count
    }
    _myResources = next
    _notify()
  },

  clearGame() {
    _game           = null
    _players        = []
    _hexes          = []
    _vertices       = []
    _edges          = []
    _ports          = []
    _myResources    = {}
    _resourceCounts = {}
    _lastDice       = null
    _pendingDiscards = {}
    _myDiscardRequired = 0
    _myDevCards         = []
    _devDeckRemaining   = 25
    _largestArmyUserCode = null
    _otherDevCardCounts = {}
    // 단계 5
    _longestRoadUserCode = null
    _allScores           = []
    _myVictoryPoints     = null
    _gameEnded           = null
    // Phase 5 거래
    _openTrades          = []
    _myResponses         = {}
    _counterUsedTrades   = new Set()
    _tradeAllResponses   = {}
    _notify()
  },
}
